<script setup>
import { format, isValid, parse } from 'date-fns';
import { computed, ref } from 'vue';
import { CsIcon } from '../icon';
import { CsInput } from '../input';
import {
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
} from '../popover';
import CsDatePickerPanel from './CsDatePickerPanel.vue';

/**
 * @typedef {'bottom-start'|'top'|'right'|'bottom'|'left'|'top-start'|'top-end'|'right-start'|'right-end'|'bottom-end'|'left-start'|'left-end'} TooltipPlacement
 */

/**
 * @typedef {object} CsDatePickerProps
 * @property {Date} [value=null] - The selected date value (for single date mode)
 * @property {Date} [initialDate=new Date()] - Initial date to display (defaults to today)
 * @property {Date} [disableDatesBefore=null] - Disable all dates before this date (exclusive)
 * @property {Date} [disableDatesAfter=null] - Disable all dates after this date (exclusive)
 * @property {Date[]} [disabledDates=[]] - Array of specific dates to disable
 * @property {boolean} [isRange=false] - Enable date range selection mode
 * @property {Date} [startDate=null] - Start date of the range (for range mode)
 * @property {Date} [endDate=null] - End date of the range (for range mode)
 * @property {boolean} [isMultimonth=false] - Display two months side by side
 * @property {string} [placeholder=''] - Placeholder text for the input
 * @property {string} [label=''] - Label text for the input
 * @property {string} [labelTooltip=''] - Tooltip text for the label
 * @property {TooltipPlacement} [labelTooltipPlacement='right'] - Placement of the label tooltip
 * @property {string} [helpText=''] - Help text displayed below the input
 * @property {string} [name=''] - Name attribute for the input
 * @property {string} [id=''] - ID attribute for the input
 * @property {boolean} [isRequired=false] - Whether the input is required
 * @property {boolean} [isDisabled=false] - Whether the input is disabled
 * @property {boolean} [isReadonly=false] - Whether the input is read-only
 * @property {string} [error=''] - Error message for the input
 * @property {'default'|'sm'} [size='default'] - Size of the input
 * @property {boolean} [isClearable=true] - Whether the input can be cleared
 */

/**
 * @type {CsDatePickerProps}
 */
const props = defineProps({
  value: {
    type: Date,
    default: null,
  },
  initialDate: {
    type: Date,
    default: () => new Date(),
  },
  disableDatesBefore: {
    type: Date,
    default: null,
  },
  disableDatesAfter: {
    type: Date,
    default: null,
  },
  disabledDates: {
    type: Array,
    default: () => [],
  },
  isRange: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: [String, Date],
    default: null,
  },
  endDate: {
    type: [String, Date],
    default: null,
  },
  isMultimonth: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  labelTooltip: {
    type: String,
    default: '',
  },
  labelTooltipPlacement: {
    type: String,
    default: 'right',
  },
  helpText: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isReadonly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'default',
  },
  isClearable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'input',
  'change',
  'select',
  'range-select',
  'clear',
]);

// Date validation utilities
function isValidDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return false;

  // Check if it matches yyyy-MM-dd format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  // Parse and validate the actual date
  const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
  return isValid(parsedDate);
}

function formatDateForDisplay(date) {
  if (!date) return '';

  // Handle string dates
  if (typeof date === 'string') {
    if (!isValidDateString(date)) return '';
    date = parse(date, 'yyyy-MM-dd', new Date());
  }

  // Handle Date objects
  if (!isValid(date)) return '';

  return format(date, 'MMM dd, yyyy');
}

function formatDateForEdit(date) {
  if (!date) return '';

  // Handle string dates
  if (typeof date === 'string') {
    if (!isValidDateString(date)) return '';
    date = parse(date, 'yyyy-MM-dd', new Date());
  }

  // Handle Date objects
  if (!isValid(date)) return '';

  return format(date, 'yyyy-MM-dd');
}

function parseInputDate(dateString) {
  if (!dateString || !isValidDateString(dateString)) return null;
  return parse(dateString, 'yyyy-MM-dd', new Date());
}

// Component state
const selectedDate = ref(props.value);
const isInputFocused = ref(false);
const tempInputValue = ref('');
const isPanelOpen = ref(false);

// Input ref for programmatic focus/blur
const inputRef = ref(null);

// Mouse tracking to determine if user is inside the date picker area
const isInsideDatePicker = ref(false);

// Flag to track when we intentionally want to close (e.g., after date selection)
const isIntentionalClose = ref(false);

// Computed display value
const inputDisplay = computed(() => {
  if (isInputFocused.value) {
    return tempInputValue.value;
  }

  if (props.isRange) {
    const startDisplay = formatDateForDisplay(props.startDate);
    const endDisplay = formatDateForDisplay(props.endDate);

    if (startDisplay && endDisplay) {
      return `${startDisplay} - ${endDisplay}`;
    }
    if (startDisplay) {
      return startDisplay;
    }
    if (endDisplay) {
      return endDisplay;
    }
    return '';
  }

  return formatDateForDisplay(selectedDate.value);
});

// Popover handlers
function handleOpen() {
  isPanelOpen.value = true;
}

function handleClose() {
  isPanelOpen.value = false;
  // Force blur the input when popover closes
  if (inputRef.value && inputRef.value.input) {
    inputRef.value.blur();
  }
}

// Mouse tracking handlers
function handleMouseEnter() {
  isInsideDatePicker.value = true;
}

function handleMouseLeave() {
  isInsideDatePicker.value = false;
}

// Input handlers
function handleInputFocus() {
  // Don't open popover if disabled or readonly
  if (props.isDisabled || props.isReadonly) {
    return;
  }

  // Reset intentional close flag when user manually focuses
  isIntentionalClose.value = false;

  isInputFocused.value = true;

  if (props.isRange) {
    // For range mode, we'll need to implement range input handling
    // For now, show empty to allow manual input
    tempInputValue.value = '';
  } else {
    tempInputValue.value = formatDateForEdit(selectedDate.value);
  }

  // Open popover when input is focused
  handleOpen();
}

function handleInputBlur() {
  // If this is an intentional close (e.g., after date selection), don't refocus
  if (isIntentionalClose.value) {
    isIntentionalClose.value = false; // Reset the flag
    isInputFocused.value = false;
    return;
  }

  // If mouse is still inside the date picker area, refocus the input
  if (isInsideDatePicker.value) {
    // Use setTimeout to ensure the blur event completes before refocusing
    setTimeout(() => {
      if (inputRef.value && inputRef.value.input) {
        inputRef.value.focus();
      }
    }, 10);
    return;
  }

  // Proceed with normal blur handling when mouse is outside
  isInputFocused.value = false;

  if (props.isRange) {
    // Handle range input in future enhancement
    tempInputValue.value = '';
    return;
  }

  const inputValue = tempInputValue.value.trim();

  // Handle empty input
  if (!inputValue) {
    selectedDate.value = null;
    emit('input', null);
    emit('change', null);
    // Set intentional close flag before closing
    isIntentionalClose.value = true;
    // Close popover when input is cleared
    handleClose();
    return;
  }

  // Validate and update if valid
  if (isValidDateString(inputValue)) {
    const parsedDate = parseInputDate(inputValue);
    if (parsedDate) {
      selectedDate.value = parsedDate;
      emit('input', parsedDate);
      emit('change', parsedDate);
      emit('select', parsedDate);
      // Set intentional close flag before closing
      isIntentionalClose.value = true;
      // Close popover after successful manual input
      handleClose();
    }
  } else {
    // Invalid date, revert to previous value
    tempInputValue.value = formatDateForEdit(selectedDate.value);
  }
}

function handleInputChange(value) {
  tempInputValue.value = value;

  if (props.isRange) {
    // Skip real-time validation for range mode for now
    return;
  }

  // Real-time validation for single date mode (but don't close popover immediately)
  if (isValidDateString(value)) {
    const parsedDate = parseInputDate(value);
    if (parsedDate) {
      selectedDate.value = parsedDate;
      emit('input', parsedDate);
      emit('change', parsedDate);
      emit('select', parsedDate);
      // Don't close popover here - let user finish typing or blur naturally
    }
  }
}

function handleInputClear() {
  selectedDate.value = null;
  emit('input', null);
  emit('change', null);
  emit('clear');

  // Set intentional close flag before closing
  isIntentionalClose.value = true;
  // Close popover when input is cleared
  handleClose();
}

// Panel handlers
function handleDateSelect(date) {
  selectedDate.value = date;
  emit('input', date);
  emit('change', date);
  emit('select', date);

  // Set intentional close flag before closing
  isIntentionalClose.value = true;
  // Close popover and blur input after date selection
  handleClose();
}

function handleRangeSelect(range) {
  emit('range-select', range);

  // For range mode, only close after both dates are selected
  if (range.startDate && range.endDate) {
    // Set intentional close flag before closing
    isIntentionalClose.value = true;
    handleClose();
  }
}
</script>

<template>
  <CsPopover
    v-slot="{ isOpen }"
    class="inline-flex"
    :is-open="isPanelOpen"
    :is-manual="true"
    @click-outside="handleClose"
  >
    <CsPopoverTrigger
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <CsInput
        :id="id"
        ref="inputRef"
        :value="inputDisplay"
        :placeholder="
          isInputFocused
            ? 'YYYY-MM-DD'
            : placeholder || (isRange ? 'Select date range' : 'Select date')
        "
        :label="label"
        :label-tooltip="labelTooltip"
        :label-tooltip-placement="labelTooltipPlacement"
        :help-text="helpText"
        :name="name"
        :is-required="isRequired"
        :is-disabled="isDisabled"
        :is-readonly="isReadonly"
        :error="error"
        :size="size"
        :is-clearable="isClearable"
        :data-pressed="isOpen"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @input="handleInputChange"
        @clear="handleInputClear"
      >
        <template #prefix>
          <CsIcon name="calendar" />
        </template>
      </CsInput>
    </CsPopoverTrigger>

    <CsPopoverContent
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <CsPopoverCard>
        <CsPopoverCardContent class="p-3">
          <CsDatePickerPanel
            :value="selectedDate"
            :initial-date="initialDate"
            :disable-dates-before="disableDatesBefore"
            :disable-dates-after="disableDatesAfter"
            :disabled-dates="disabledDates"
            :is-range="isRange"
            :start-date="startDate"
            :end-date="endDate"
            :is-multimonth="isMultimonth"
            @input="handleDateSelect"
            @change="handleDateSelect"
            @select="handleDateSelect"
            @range-select="handleRangeSelect"
          />
        </CsPopoverCardContent>
      </CsPopoverCard>
    </CsPopoverContent>
  </CsPopover>
</template>
